/**
 * Google Analytics 4 com consentimento (LGPD): nada do Google carrega antes de a pessoa
 * aceitar no banner. O ID vem do `config.json` (`analytics.ga4`), nunca do código.
 */

export type Consent = "granted" | "denied";
export interface AnalyticsItem {
  item_id: string;
  item_name: string;
  item_category?: string;
  price?: number;
  quantity?: number;
}

const CONSENT_KEY = "crialume:analytics-consent";
const PURCHASE_KEY = "crialume:analytics-purchase:";
const listeners = new Set<() => void>();
let measurementId: string | null = null;
let loaded = false;

type Gtag = (...args: unknown[]) => void;
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

export function readConsent(): Consent | null {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

export function subscribeConsent(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Liga o GA4 para este ID. Sem ID, tudo aqui vira nada. */
export function configureAnalytics(
  id: string | undefined,
  hostname: string = window.location.hostname,
) {
  // Desenvolvimento local não entra nos números do site.
  const local = ["localhost", "127.0.0.1", "[::1]"].includes(hostname);
  measurementId = id && /^G-[A-Z0-9]+$/.test(id) && !local ? id : null;
  if (measurementId && readConsent() === "granted") load();
}

export function analyticsEnabled() {
  return measurementId !== null;
}

export function setConsent(consent: Consent) {
  try {
    localStorage.setItem(CONSENT_KEY, consent);
  } catch {
    /* Sem armazenamento, a escolha vale só nesta visita. */
  }
  if (consent === "granted") {
    load();
    trackPage(window.location);
  }
  listeners.forEach((listener) => listener());
}

function active() {
  return measurementId !== null && readConsent() === "granted";
}

function load() {
  if (loaded || !measurementId) return;
  loaded = true;
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag() {
    // O gtag.js lê o objeto `arguments`, não um array.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag("js", new Date());
  // As páginas são enviadas pelo roteador, já sem tokens na URL.
  window.gtag("config", measurementId, { send_page_view: false });
  const inject = () => {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
  };
  // Depois da página pronta, para não competir com o carregamento.
  if (document.readyState === "complete") schedule(inject);
  else window.addEventListener("load", () => schedule(inject), { once: true });
}

function schedule(run: () => void) {
  if ("requestIdleCallback" in window) window.requestIdleCallback(run, { timeout: 3000 });
  else setTimeout(run, 1);
}

/** Convite e confirmação de e-mail levam um token na URL: o GA só vê o tipo de página. */
export function safePath(pathname: string) {
  return pathname
    .replace(/^\/convite\/[^/]+/, "/convite/:token")
    .replace(/^\/confirmar-email\/[^/]+/, "/confirmar-email/:token");
}

/** Só as UTMs passam adiante: atribuição de campanha, sem ids de pedido. */
export function safeSearch(search: string) {
  const params = new URLSearchParams(search);
  const kept = new URLSearchParams();
  params.forEach((value, key) => {
    if (key.startsWith("utm_") || key === "gclid") kept.append(key, value);
  });
  const text = kept.toString();
  return text ? `?${text}` : "";
}

export function trackPage(location: { pathname: string; search: string }) {
  if (!active()) return;
  const path = safePath(location.pathname);
  window.gtag!("event", "page_view", {
    page_path: path,
    page_location: `${window.location.origin}${path}${safeSearch(location.search)}`,
    page_title: document.title,
  });
}

export function track(name: string, params: Record<string, unknown> = {}) {
  if (!active()) return;
  window.gtag!("event", name, params);
}

/** A compra conta uma vez por pedido, mesmo se a pessoa voltar à tela. */
export function trackPurchase(orderId: string, params: Record<string, unknown>) {
  if (!active()) return;
  try {
    if (localStorage.getItem(PURCHASE_KEY + orderId)) return;
    localStorage.setItem(PURCHASE_KEY + orderId, "1");
  } catch {
    /* Segue: no pior caso a mesma compra aparece duas vezes. */
  }
  track("purchase", { transaction_id: orderId, ...params });
}

export const cents = (value: number) => Math.round(value) / 100;
